import React, { useMemo, useRef } from 'react';
import ReactToPrint from "react-to-print";
import parse from "html-react-parser";

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { setActiveEmailTab, setIsRefreshed, showAttachmentFunc } from '../../../config/redux/slice/appSlice';
import { getExtension, getFilename } from '../../../helpers/helpers';
import { appApi } from '../../../utils/endpoints/appApi';
import { appUrls } from '../../../utils/urls/appUrls';
import { PrintInbox } from "../Print/PrintInbox";
import { image_extensions } from "../../../constants/english";
import { convertDateTime, formatUserRole } from "../../../utils/lib";
import { ROLES } from '../../../constants';

import AttachmentView from './AttachmentView';
import DeleteDigitalLetter from './DeleteDigitalLetter';

export default function DigitalLetterDetails() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const inboxPrintRef = useRef(null);

    const { digitalLetterManagement: { inboxLetterDetails, showDeleteConfirmationForm, showAttachmentModal }} = useSelector((state) => state.app)
    const { userData } = useSelector((state) => state.authData)
    const { staff } = useSelector((state) => state.notapUser);

    const recipientStaffEmail = useMemo(() => {
      if (inboxLetterDetails?.RECEIVER_ID) {
        const recipientStaff = staff.find((user) => user.EMAIL === inboxLetterDetails.RECEIVER_ID);
        if (recipientStaff) return formatUserRole(recipientStaff.USER_ROLE_TEXT);
      }

      return null;
    }, [inboxLetterDetails?.RECEIVER_ID, staff]);

    const navigateToInboxList = () =>{
      navigate(`${appUrls.digitalLetterBase}?tab=inbox`)
    };

    const handleGoBack = ()=>{
      navigate(`${appUrls.digitalLetterBase}?tab=inbox`)
      dispatch(setActiveEmailTab({ name: 'inbox' }))
    };

    const handleViewLetterHistory = () => {
        dispatch(setIsRefreshed('done'))
        navigate(`/notap/digital-letters/${inboxLetterDetails?.DIGITAL_LETTER_ID}/evaluation-history`)
    };

    return (
          <>
            {showDeleteConfirmationForm && (
              <DeleteDigitalLetter
                  adjective={'from'}
                  email={inboxLetterDetails?.SENDER_EMAIL}
                  id={inboxLetterDetails?.ID}
                  subject={inboxLetterDetails?.LETTER_SUBJECT}
                  url={appApi.deleteLetter(inboxLetterDetails?.ID)}
                  callBackFunc={navigateToInboxList}
              />
            )}

            {showAttachmentModal && <AttachmentView filePath={inboxLetterDetails?.ATTACH_DOCUMENTS} />}

            <div className="email-right-column p-4 p-xl-5">
              {/* <!-- Email Right Header --> */}
              <div className="email-right-header mb-2">
                {/* <!-- head left option --> */}
                <div className="head-left-options">
                  <button type="button" onClick={handleGoBack} className="btn btn-icon btn-outline btn-rounded-circle">
                    <i className="mdi mdi-arrow-left"></i>
                  </button>
                </div>
                {/* <!-- head right option --> */}
                <div style={{columnGap: '0.5rem'}} className="d-flex align-items-center head-right-options">
                    <button 
                      onClick={handleViewLetterHistory}
                      type="button" 
                      className="btn-sm btn-primary"
                    >
                      <i className="mdi mdi-eye mr-1"></i>
                      <span>View Letter History</span>
                    </button>
                    
                    {inboxLetterDetails?.NOTAP_STAFF_USER === userData?.email && (
                      <button 
                        onClick={() => navigate(`${appUrls.digitalLetterBase}?tab=inbox&details=true&edit=true`)}
                        type="button" 
                        className="btn-sm btn-primary"
                      >
                        <i className="mdi mdi-square-edit-outline mr-1"></i>
                        <span> Add Evaluation</span>
                      </button>
                    )}
                </div>
              </div>
              <div className="border rounded email-details">

                <div className="email-details-header">
                  <h4 className="text-dark">
                    {inboxLetterDetails?.LETTER_SUBJECT ? inboxLetterDetails.LETTER_SUBJECT : '(no subject)'}
                  </h4>
                </div>

                <div className="email-details-content">
                  <div className="email-details-content-header">

                    <div className="media media-sm mb-lg-0">
                      <div className="media-sm-wrapper">
                        <div className='email-avatar'>
                        <p className='text-capitalize'>{inboxLetterDetails?.SENDER_NAME_1[0]}</p>
                      </div>
                    </div>
                      
                      
                    <div className="media-body">
                      <h6 className="mt-0 text-dark font-weight-bold">{inboxLetterDetails?.SENDER_NAME_1}</h6>
                      <div>
                        From: 
                        <i className="mdi mdi-chevron-left"></i>{inboxLetterDetails?.SENDER_ID_1}
                        <i className="mdi mdi-chevron-right"></i>
                      </div>
                      {recipientStaffEmail && (
                        <div>
                          To: <i className="mdi mdi-chevron-left"></i>{recipientStaffEmail}<i className="mdi mdi-chevron-right"></i>
                        </div>
                      )}
                    </div>
                    </div>

                    <div className="email-details-content-header-right">
                      <span className="p-1 p-xl-2">{convertDateTime(inboxLetterDetails.CREATED_AT, "MMM DD, YYYY, HH:mm A")}</span>

                      {inboxLetterDetails?.ATTACH_DOCUMENTS && (
                        <button className="text-color p-1 p-xl-2" onClick={() => dispatch(showAttachmentFunc({modal: true}))}>
                          <i className="mdi mdi-attachment"></i>
                        </button>
                      )}

                      <div className="btn-group p-1 p-xl-2" role="group" aria-label="Basic example">
                        <ReactToPrint
                          content={() => inboxPrintRef.current}
                          trigger={() => (
                            <button title="Print Inbox" type="button" className="btn btn-pill">
                              <i className="mdi mdi-printer"></i>
                            </button>
                          )}
                        />
                      </div>
                        <div style={{ display: "none" }}>
                          <PrintInbox ref={inboxPrintRef} />
                        </div>
                    </div>
                  </div>

                  <div style={{minHeight: '70vh', maxHeight: '100vh', overflowY: 'auto'}}>
                  {inboxLetterDetails?.LETTER_CONTENTS && <div>{parse(inboxLetterDetails.LETTER_CONTENTS)}</div>}  
                  {inboxLetterDetails?.DIGITAL_SIGNATURE && (
                    <div className='mt-4'>
                      <img 
                        className='digital-signature-img' 
                        src={inboxLetterDetails?.DIGITAL_SIGNATURE} 
                        alt='letter signature'
                      />
                    </div>
                  )}
                  {inboxLetterDetails?.ATTACH_DOCUMENTS && (
                    <div className='pt-7 col-12 col-md-6 col-lg-5'>
                      <h6 className='mb-2'><i className="mdi mdi-paperclip ml-2"></i> Attachment</h6>
                      <div style={{position:'relative'}} className='border d-flex justify-content-between attachment-container p-2'>
                        <div style={{columnGap: '0.5rem'}} className="attachment-overlay px-2">
                          <p className="font-weight-bold">Download File</p>
                            <button 
                              title="Download Attachment"  
                              style={{fontSize: '2rem'}}
                            >
                              <a 
                                className='text-smoke mdi mdi-arrow-down-bold-box'
                                target="_blank" 
                                href={inboxLetterDetails?.ATTACH_DOCUMENTS} 
                                rel="noreferrer noopenner" 
                                download={getFilename(inboxLetterDetails?.ATTACH_DOCUMENTS)}
                              >
                              </a>
                            </button>
                        </div>
                      
                      <div style={{columnGap: '0.5rem', maxWidth: '93%'}} className='d-flex align-items-center'>
                        {image_extensions.includes((getExtension(inboxLetterDetails?.ATTACH_DOCUMENTS)).toLowerCase()) 
                            ? <i style={{fontSize: '2rem'}} className="mdi mdi-image"></i> 
                            : <i style={{fontSize: '2rem'}} className="mdi mdi-file-pdf-box"></i>
                        }
                        <span className="file-name-display">{getFilename(inboxLetterDetails?.ATTACH_DOCUMENTS)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  </div>
                </div>
              </div>
            </div>
      </>   
    );
}
